import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  LinearProgress,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HomeIcon from '@mui/icons-material/Home';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimerIcon from '@mui/icons-material/Timer';

const QuestionCard = ({ ...rest }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    instanceLabName,
    assessmentId,
    assessmentLinkName,
    instanceLabId,
    traineeId,
    stimer,
    temptimeout,
  } = location.state || {};

  const [quiz, setQuiz] = useState({
    details: [],
    currentQuestionIndex: 0,
    userAnswers: [],
    currentQuestionAnswer: [],
  });
  const [selectedOption, setSelectedOption] = useState('');
  const [score, setScore] = useState(0);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [timer, setTimer] = useState(0);
  const [timeout, setTimeOut1] = useState(temptimeout || 1);
  const [totalTime, setTotalTime] = useState(0);

  useEffect(() => {
    if (!assessmentLinkName || !assessmentId) {
      setError('Missing assessment information. Please go back and try again.');
      setLoading(false);
      return;
    }

    if (timeout === 1) {
      let isMounted = true;
      setLoading(true);
      setError(null);

      fetch(`${process.env.REACT_APP_API_URL}/api/quizlist/${assessmentLinkName}/${assessmentId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
      })
        .then((resp) => resp.json())
        .then((data) => {
          if (!isMounted) return;

          if (data.length > 0) {
            if (data[0].quizdetails === 'Already Assessment Completed') {
              setAlreadyCompleted(true);
              setIsQuizCompleted(true);
            } else if (data[0].quizdetails && Array.isArray(data[0].quizdetails)) {
              const shuffledQuestions = [...data[0].quizdetails].sort(() => Math.random() - 0.5);
              setQuiz((prev) => ({
                ...prev,
                details: shuffledQuestions,
              }));

              if (data[1]?.append_count && stimer) {
                const calculatedTime = data[1].append_count * stimer;
                setTotalTime(calculatedTime);
                setTimer(calculatedTime);
              }
            } else {
              setError('No quiz questions found. Please contact your administrator.');
            }
          } else {
            setError('No data received from the server. Please try again later.');
          }
        })
        .catch((err) => {
          if (isMounted) {
            setError('Failed to load assessment questions. Please try again.');
          }
        })
        .finally(() => {
          if (isMounted) {
            setLoading(false);
            setTimeOut1(2);
          }
        });

      return () => {
        isMounted = false;
      };
    }
  }, [assessmentLinkName, assessmentId, timeout, stimer]);

  useEffect(() => {
    if (timer > 0 && !isQuizCompleted && timeout === 2) {
      const interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    } else if (timer === 0 && !isQuizCompleted && quiz.details.length > 0) {
      const userAnswer = selectedOption || '';
      const currentQuestion = quiz.details[quiz.currentQuestionIndex];
      const isAnswerCorrect = userAnswer === currentQuestion?.Answer.toString();

      const finalScore = isAnswerCorrect ? score + 1 : score;
      setScore(finalScore);

      onSubmitClick(userAnswer, finalScore, true);
      setIsQuizCompleted(true);
    }
  }, [timer, isQuizCompleted, quiz.details, quiz.currentQuestionIndex, selectedOption, score]);

  const handleOptionChange = useCallback((event) => {
    setSelectedOption(event.target.value);
  }, []);

  const handleNextChange = useCallback(() => {
    if (!selectedOption && quiz.currentQuestionIndex < quiz.details.length - 1) {
      return;
    }

    const currentQuestion = quiz.details[quiz.currentQuestionIndex];
    const userAnswer = selectedOption || '';
    const isAnswerCorrect = userAnswer === currentQuestion.Answer.toString();

    const newScore = isAnswerCorrect ? score + 1 : score;
    const isLastQuestion = quiz.currentQuestionIndex === quiz.details.length - 1;

    if (isLastQuestion) {
      setSubmitting(true);
      onSubmitClick(userAnswer, newScore, false);
    } else {
      setQuiz((prev) => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex + 1,
        userAnswers: [...prev.userAnswers, userAnswer],
        currentQuestionAnswer: [...prev.currentQuestionAnswer, currentQuestion.Answer],
      }));
      setScore(newScore);
      setSelectedOption('');
    }
  }, [selectedOption, quiz, score]);

  const onSubmitClick = useCallback(
    (latestAnswer, calculatedScore, isAutoSubmit = false) => {
      const finalScore =
        calculatedScore +
        (latestAnswer === quiz.details[quiz.details.length - 1]?.Answer.toString() ? 1 : 0);

      const data = {
        userAnswer: [...quiz.userAnswers, latestAnswer],
        labId: instanceLabId,
        assessmentName: assessmentLinkName,
        trainee_name: traineeId,
        assessmentId: assessmentId,
        score: finalScore,
      };

      fetch(
        `${process.env.REACT_APP_API_URL}/api/quizanswersubmit/${instanceLabId}/${assessmentLinkName}/${traineeId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('token'),
          },
          body: JSON.stringify(data),
        }
      )
        .then((resp) => resp.json())
        .then((data) => {
          if (data.code === 500) {
            setSubmitError(data.message || 'Failed to submit assessment. Please try again.');
            setSubmitting(false);
            if (!isAutoSubmit) {
              setIsQuizCompleted(false);
            }
          } else {
            setScore(finalScore);
            setIsQuizCompleted(true);
            setSubmitting(false);
          }
        })
        .catch(() => {
          setSubmitError('Something went wrong while submitting. Please try again.');
          setSubmitting(false);
          if (!isAutoSubmit) {
            setIsQuizCompleted(false);
          }
        });
    },
    [quiz.userAnswers, instanceLabId, assessmentLinkName, traineeId, assessmentId, quiz.details]
  );

  const handlePreviousChange = useCallback(() => {
    if (quiz.currentQuestionIndex > 0) {
      const previousIndex = quiz.currentQuestionIndex - 1;
      setQuiz((prev) => ({
        ...prev,
        currentQuestionIndex: previousIndex,
      }));
      setSelectedOption(quiz.userAnswers[previousIndex] || '');
    }
  }, [quiz.currentQuestionIndex, quiz.userAnswers]);

  const currentQuestion = useMemo(() => {
    if (quiz.details.length > quiz.currentQuestionIndex) {
      return quiz.details[quiz.currentQuestionIndex];
    }
    return null;
  }, [quiz.details, quiz.currentQuestionIndex]);

  const progressPercentage = useMemo(() => {
    if (quiz.details.length === 0) return 0;
    return ((quiz.currentQuestionIndex + 1) / quiz.details.length) * 100;
  }, [quiz.currentQuestionIndex, quiz.details.length]);

  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const handleBackToAssessments = useCallback(() => {
    navigate('/app/assessmentlist');
  }, [navigate]);

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ py: 6, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3} alignItems="center">
              <CircularProgress />
              <Typography variant="h6" color="text.secondary">
                Loading assessment questions...
              </Typography>
              <Box sx={{ width: '100%' }}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
                <Stack spacing={1} sx={{ mt: 2 }}>
                  <Skeleton variant="text" width="100%" height={40} />
                  <Skeleton variant="text" width="80%" height={40} />
                  <Skeleton variant="text" width="60%" height={40} />
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={{ py: 6, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToAssessments}
            >
              Back to Assessments
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (isQuizCompleted) {
    const percentageScore = quiz.details.length > 0 ? (score / quiz.details.length) * 100 : 0;
    const isHighScore = percentageScore >= 50;

    return (
      <Container maxWidth={false} sx={{ py: 6, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
        <Card sx={{ maxWidth: { xs: '100%', sm: '100%', md: '900px', lg: '1000px', xl: '1200px' }, mx: 'auto' }}>
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(46,125,50,0.12), rgba(21,101,192,0.12))',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              p: { xs: 2.5, md: 3 },
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
              <SchoolIcon color="primary" />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Lab name
                </Typography>
                <Typography variant="h6">{instanceLabName || 'N/A'}</Typography>
              </Box>
            </Stack>
          </Box>

          <Divider />

          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            {alreadyCompleted ? (
              <>
                <Alert severity="info" sx={{ mb: 3 }}>
                  You have already completed this assessment.
                </Alert>
              </>
            ) : (
              <>
                <CheckCircleIcon
                  sx={{
                    fontSize: 80,
                    color: isHighScore ? 'success.main' : 'warning.main',
                    mb: 2,
                  }}
                />
                <Typography variant="h4" gutterBottom>
                  Assessment Completed!
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  {assessmentLinkName?.replace(/,/g, '') || 'Assessment'}
                </Typography>

                <Box
                  sx={{
                    display: 'inline-block',
                    p: 3,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: 2,
                    borderColor: isHighScore ? 'success.main' : 'warning.main',
                    mb: 3,
                  }}
                >
                  <Typography variant="h2" color={isHighScore ? 'success.main' : 'warning.main'}>
                    {score}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    out of {quiz.details.length}
                  </Typography>
                  <Chip
                    label={`${percentageScore.toFixed(0)}%`}
                    color={isHighScore ? 'success' : 'warning'}
                    sx={{ mt: 1 }}
                  />
                </Box>

                <Typography
                  variant="body1"
                  color={isHighScore ? 'success.main' : 'warning.main'}
                  sx={{ mb: 3 }}
                >
                  {isHighScore
                    ? 'Great job! You passed the assessment.'
                    : 'Keep practicing to improve your score.'}
                </Typography>
              </>
            )}

            <Button
              variant="contained"
              size="large"
              startIcon={<HomeIcon />}
              onClick={handleBackToAssessments}
              sx={{ mt: 2 }}
            >
              Back to Assessments
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 4, pb: 10, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
      <Card {...rest} sx={{ maxWidth: { xs: '100%', sm: '100%', md: '900px', lg: '1000px', xl: '1200px' }, mx: 'auto' }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(46,125,50,0.12), rgba(21,101,192,0.12))',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            p: { xs: 2.5, md: 3 },
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <SchoolIcon color="primary" />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Lab name
                </Typography>
                <Typography variant="h6">{instanceLabName || 'N/A'}</Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              <Chip
                icon={<QuizIcon />}
                label={assessmentLinkName?.replace(/,/g, '') || 'Assessment'}
                color="primary"
                variant="outlined"
              />
              {timer > 0 && (
                <Chip
                  icon={<TimerIcon />}
                  label={`Time: ${formatTime(timer)}`}
                  color={timer <= 60 ? 'error' : timer <= 180 ? 'warning' : 'default'}
                  variant="filled"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                  }}
                />
              )}
              {stimer && !timer && (
                <Chip
                  icon={<AccessTimeIcon />}
                  label={`${stimer} min per question`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </Stack>
        </Box>

        <Divider />

        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Question {quiz.currentQuestionIndex + 1} of {quiz.details.length}
              </Typography>
              <Chip
                label={`${progressPercentage.toFixed(0)}%`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          {timer === 0 && !isQuizCompleted && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Time's up! Your assessment is being submitted automatically.
            </Alert>
          )}

          {currentQuestion ? (
            <>
              <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
                {currentQuestion.Question}
              </Typography>

              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={selectedOption}
                  onChange={handleOptionChange}
                  name="question-options"
                >
                  {currentQuestion.Option1 && (
                    <FormControlLabel
                      value={currentQuestion.Option1}
                      control={<Radio />}
                      label={currentQuestion.Option1}
                      sx={{
                        mb: 1,
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: selectedOption === currentQuestion.Option1 ? 'primary.main' : 'divider',
                        bgcolor: selectedOption === currentQuestion.Option1 ? 'action.selected' : 'transparent',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    />
                  )}
                  {currentQuestion.Option2 && (
                    <FormControlLabel
                      value={currentQuestion.Option2}
                      control={<Radio />}
                      label={currentQuestion.Option2}
                      sx={{
                        mb: 1,
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: selectedOption === currentQuestion.Option2 ? 'primary.main' : 'divider',
                        bgcolor: selectedOption === currentQuestion.Option2 ? 'action.selected' : 'transparent',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    />
                  )}
                  {currentQuestion.Option3 && (
                    <FormControlLabel
                      value={currentQuestion.Option3}
                      control={<Radio />}
                      label={currentQuestion.Option3}
                      sx={{
                        mb: 1,
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: selectedOption === currentQuestion.Option3 ? 'primary.main' : 'divider',
                        bgcolor: selectedOption === currentQuestion.Option3 ? 'action.selected' : 'transparent',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    />
                  )}
                  {currentQuestion.Option4 && (
                    <FormControlLabel
                      value={currentQuestion.Option4}
                      control={<Radio />}
                      label={currentQuestion.Option4}
                      sx={{
                        mb: 1,
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: selectedOption === currentQuestion.Option4 ? 'primary.main' : 'divider',
                        bgcolor: selectedOption === currentQuestion.Option4 ? 'action.selected' : 'transparent',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    />
                  )}
                </RadioGroup>
              </FormControl>
            </>
          ) : (
            <Alert severity="warning">
              No question available. Please contact your administrator.
            </Alert>
          )}
        </CardContent>

        <Divider />

        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            zIndex: 1,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handlePreviousChange}
            disabled={quiz.currentQuestionIndex === 0 || submitting || timer === 0}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            endIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : quiz.currentQuestionIndex === quiz.details.length - 1 ? (
                <CheckCircleIcon />
              ) : (
                <ArrowForwardIcon />
              )
            }
            onClick={handleNextChange}
            disabled={submitting || timer === 0 || (!selectedOption && quiz.currentQuestionIndex < quiz.details.length - 1)}
          >
            {submitting
              ? 'Submitting...'
              : quiz.currentQuestionIndex === quiz.details.length - 1
              ? 'Submit Assessment'
              : 'Next'}
          </Button>
        </Box>
      </Card>
    </Container>
  );
};

export default QuestionCard;
